import { useQuery } from '@tanstack/react-query'
import { AlertCircle, Download, Trophy } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '../api'
import { KeyboardShortcuts, type ShortcutMap } from '../components/KeyboardShortcuts'
import { PassRateBar, ScoreBar } from '../components/ScoreBar'
import { Sparkline } from '../components/Sparkline'
import type { LeaderboardRow, Tier } from '../types'

// Default leaderboard window (days). Matches backend default for the export
// endpoint added by D2.
const DEFAULT_WINDOW_DAYS = 30
const API_KEY_STORAGE = 'killacoach_api_key'

// Original 5 rubric metrics. Headers are the column labels (kept short to
// fit the table even with 5 new columns added beside them).
const METRIC_ORDER = [
  'talk_ratio',
  'longest_monologue',
  'next_step_commitment',
  'objection_delta',
  'dispo_alignment',
] as const

// Wave-1 1000x metrics. May be missing on older payloads; UI renders '--'.
const WAVE1_METRIC_ORDER = [
  'localize_within_15s',
  'free_word_density',
  'time_to_pitch',
  'name_personalization_rate',
  'seven_step_coverage',
] as const

const METRIC_SHORT: Record<string, string> = {
  talk_ratio: 'talk',
  longest_monologue: 'monologue',
  next_step_commitment: 'next-step',
  objection_delta: 'objection',
  dispo_alignment: 'dispo',
  localize_within_15s: 'localize',
  free_word_density: 'free',
  time_to_pitch: 'pitch-time',
  name_personalization_rate: 'name-rate',
  seven_step_coverage: '7-step',
}

// Tier color map. T1 green, T2 blue, T3 yellow, T4 red.
const TIER_BADGE_CLASS: Record<Tier, string> = {
  1: 'bg-emerald-900/60 text-emerald-200 border-emerald-700',
  2: 'bg-sky-900/60 text-sky-200 border-sky-700',
  3: 'bg-amber-900/60 text-amber-200 border-amber-700',
  4: 'bg-red-900/60 text-red-200 border-red-700',
}

function formatPct(rate: number | null | undefined): string {
  if (rate === null || rate === undefined) return '--'
  return `${(rate * 100).toFixed(2)}%`
}

// Build sparkline points from the per-metric pass rates we already have on
// the leaderboard payload. There is no per-call history on this endpoint
// yet, so this is a "quick vibe" trend — each point = one metric pass rate
// in METRIC_ORDER (0..100). When a metric is missing we skip it so the
// line still draws cleanly with whatever data exists.
function sparklinePointsFor(row: LeaderboardRow): number[] {
  const rates = row.pass_rates as Record<string, number | undefined>
  const pts: number[] = []
  for (const m of METRIC_ORDER) {
    const r = rates[m]
    if (typeof r === 'number') pts.push(r * 100)
  }
  return pts
}

function TierBadge({
  tier,
  tierName,
  gateFailures,
}: {
  tier: Tier | null | undefined
  tierName?: string | null
  gateFailures?: string[] | null
}) {
  if (tier === null || tier === undefined) {
    return <span className="text-xs text-slate-600">--</span>
  }
  const cls = TIER_BADGE_CLASS[tier]
  const titleParts: string[] = []
  if (tierName) titleParts.push(tierName)
  if (gateFailures && gateFailures.length > 0) {
    titleParts.push(gateFailures.join('\n'))
  }
  return (
    <span
      data-tier={tier}
      title={titleParts.join('\n') || undefined}
      className={`inline-block rounded border px-1.5 py-0.5 font-mono text-xs ${cls}`}
    >
      T{tier}
    </span>
  )
}

export function LeaderboardPage() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['leaderboard'],
    queryFn: () => api.leaderboard(),
  })

  const [downloading, setDownloading] = useState(false)
  const [downloadError, setDownloadError] = useState<string | null>(null)
  const windowDays = DEFAULT_WINDOW_DAYS

  const shortcuts: ShortcutMap = useMemo(
    () => ({ r: () => void refetch() }),
    [refetch]
  )

  async function handleDownloadCsv() {
    setDownloadError(null)
    setDownloading(true)
    try {
      let apiKey: string | null = null
      try {
        apiKey = localStorage.getItem(API_KEY_STORAGE)
      } catch {
        apiKey = null
      }
      const headers: Record<string, string> = {}
      if (apiKey) headers.Authorization = `Bearer ${apiKey}`
      const res = await fetch(
        `/api/exports/leaderboard.csv?window_days=${windowDays}`,
        { headers }
      )
      if (!res.ok) {
        if (res.status === 404) {
          setDownloadError('CSV export not available — endpoint pending')
        } else {
          setDownloadError(`CSV export failed (${res.status})`)
        }
        return
      }
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const isoDate = new Date().toISOString().slice(0, 10)
      const a = document.createElement('a')
      a.href = url
      a.download = `leaderboard_${isoDate}_${windowDays}d.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (e) {
      setDownloadError((e as Error).message || 'CSV export failed')
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className="space-y-4">
      <KeyboardShortcuts shortcuts={shortcuts} />
      <header className="flex items-center gap-2">
        <Trophy className="h-5 w-5 text-amber-400" />
        <h1 className="text-xl font-semibold">Leaderboard</h1>
        <button
          type="button"
          onClick={() => refetch()}
          className="ml-auto rounded border border-slate-700 bg-slate-900 px-2.5 py-1 text-xs text-slate-300 hover:bg-slate-800"
        >
          Refresh (R)
        </button>
        <button
          type="button"
          onClick={() => void handleDownloadCsv()}
          disabled={downloading}
          className="inline-flex items-center gap-1.5 rounded border border-slate-700 bg-slate-900 px-2.5 py-1 text-xs text-slate-300 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <Download className="h-3.5 w-3.5" />
          {downloading ? 'Downloading...' : 'Download CSV'}
        </button>
      </header>

      {downloadError && <ErrorBox msg={downloadError} />}
      {error && <ErrorBox msg={(error as Error).message} />}

      {isLoading ? (
        <TableSkeleton />
      ) : !data || data.length === 0 ? (
        <EmptyState />
      ) : (
        <LeaderboardTable rows={data} />
      )}
    </div>
  )
}

function LeaderboardTable({ rows }: { rows: LeaderboardRow[] }) {
  const navigate = useNavigate()

  // Sort: tier ASC (1→4, nulls last), then avg_overall DESC. Matches backend.
  const sortedRows = useMemo(() => {
    const copy = [...rows]
    copy.sort((a, b) => {
      const aTier = a.tier ?? Number.POSITIVE_INFINITY
      const bTier = b.tier ?? Number.POSITIVE_INFINITY
      if (aTier !== bTier) return aTier - bTier
      return (b.avg_overall ?? 0) - (a.avg_overall ?? 0)
    })
    return copy
  }, [rows])

  // All rate columns rendered between Avg-overall and Tier.
  const allMetrics = [...METRIC_ORDER, ...WAVE1_METRIC_ORDER] as const

  const goToAgent = (viciUser: string) => {
    navigate(`/agents/${encodeURIComponent(viciUser)}`)
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-slate-800 bg-slate-900/40">
      <table className="w-full text-sm">
        <thead className="bg-slate-900/80 text-xs text-slate-400">
          <tr>
            <th className="px-3 py-2 text-left">#</th>
            <th className="px-3 py-2 text-left">Agent</th>
            <th className="px-3 py-2 text-right">Calls</th>
            <th className="px-3 py-2 text-left">Avg overall</th>
            {allMetrics.map((m) => (
              <th key={m} className="px-3 py-2 text-left">
                {METRIC_SHORT[m]}
              </th>
            ))}
            <th className="px-3 py-2 text-left">Trend</th>
            <th className="px-3 py-2 text-left">Tier</th>
            <th className="px-3 py-2 text-right">Per-dial</th>
            <th className="px-3 py-2 text-right">Per-contact</th>
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((r) => {
            const points = sparklinePointsFor(r)
            return (
              <tr
                key={r.vici_user}
                tabIndex={0}
                aria-label={`View ${r.vici_user} agent detail`}
                onClick={() => goToAgent(r.vici_user)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    goToAgent(r.vici_user)
                  }
                }}
                className="cursor-pointer border-t border-slate-800 transition-colors hover:bg-slate-800/30 focus:bg-slate-800/40 focus:outline-none focus:ring-1 focus:ring-indigo-500/40"
              >
                <td className="px-3 py-2 font-mono text-slate-500">{r.rank}</td>
                <td className="px-3 py-2">
                  <Link
                    to={`/agents/${encodeURIComponent(r.vici_user)}`}
                    onClick={(e) => e.stopPropagation()}
                    className="font-medium text-slate-100 hover:text-indigo-300"
                  >
                    {r.vici_user}
                  </Link>
                  {r.full_name && (
                    <span className="ml-2 text-xs text-slate-500">{r.full_name}</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right font-mono text-slate-300">
                  {r.n_calls}
                </td>
                <td className="px-3 py-2">
                  <ScoreBar value={r.avg_overall} />
                </td>
                {allMetrics.map((m) => {
                  const rate = (r.pass_rates as Record<string, number>)[m]
                  return (
                    <td key={m} className="px-3 py-2">
                      {rate === undefined ? (
                        <span className="text-xs text-slate-600">--</span>
                      ) : (
                        <PassRateBar rate={rate} />
                      )}
                    </td>
                  )
                })}
                <td className="px-3 py-2">
                  <Sparkline points={points} tier={r.tier ?? null} />
                </td>
                <td className="px-3 py-2">
                  <TierBadge
                    tier={r.tier ?? null}
                    tierName={r.tier_name}
                    gateFailures={r.gate_failures}
                  />
                </td>
                <td className="px-3 py-2 text-right font-mono text-slate-300">
                  {formatPct(r.per_dial_rate)}
                </td>
                <td className="px-3 py-2 text-right font-mono text-slate-300">
                  {formatPct(r.per_contact_rate)}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function TableSkeleton() {
  return (
    <div className="space-y-2 rounded-lg border border-slate-800 bg-slate-900/40 p-4">
      {Array.from({ length: 6 }, (_, i) => `skeleton-${i}`).map((key) => (
        <div key={key} className="h-10 animate-pulse rounded bg-slate-800/60" />
      ))}
    </div>
  )
}

function EmptyState() {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-8 text-center">
      <p className="text-sm text-slate-400">
        No calls yet. Run{' '}
        <code className="rounded bg-slate-800 px-1.5 py-0.5 font-mono text-xs text-slate-200">
          python -m killacoach.runner.cli --agent 1201 --limit 50
        </code>
        .
      </p>
    </div>
  )
}

function ErrorBox({ msg }: { msg: string }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-red-900 bg-red-950/40 p-3 text-sm text-red-200">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{msg}</span>
    </div>
  )
}
